import React, { useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { ReactSVG } from "react-svg"
import { graphql } from "gatsby"
import { DatoCmsArticle, PniCoverFieldsFragment } from "../../../../graphql-types"
import * as styles from "./cover.module.scss"
import lensIcon from "../../../../static/vectors/pniLensIcon.svg"
import Filter from "./filter/filter"
import useWindowWidth from "../../../hooks/use-window-width"

type CoverProps = {
  data: PniCoverFieldsFragment
  filterArticles: (filteredArticles: []) => void
  articles: DatoCmsArticle[]
  searchRequestSlug: string
  searchTerms: string
  changeCurrentPage?: any
  displayFilters?: boolean
}

const Cover: React.FC<CoverProps> = ({
  data,
  filterArticles,
  articles,
  searchRequestSlug,
  searchTerms,
  changeCurrentPage,
  displayFilters = true,
}) => {
  const { register } = useForm()
  const isMobile = useWindowWidth() < 800;
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const searchFormRef = useRef<HTMLFormElement>(null)

  const handleSearchClick = (e: React.MouseEvent) => {
    if (isMobile && !isSearchExpanded) {
      e.preventDefault()
      setIsSearchExpanded(true)
    }
  }

  const handleClickOutside = (e: MouseEvent) => {
    if (isSearchExpanded && searchFormRef.current && !searchFormRef.current.contains(e.target as Node)) {
      setIsSearchExpanded(false)
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isSearchExpanded])

  return isMobile ? (
    <section className={styles.mobileContainer}>
      <form method="get" ref={searchFormRef} className={styles.mobileSearchForm} style={isSearchExpanded ? {width: "100%"} : undefined} action={`/${searchRequestSlug}`}>
        <div className={styles.mobileFormElements}>
        {isSearchExpanded && (
            <input
              id="searchWord"
              type="text"
              name="query"
              className={styles.searchInput}
              defaultValue={searchTerms}
              placeholder={data.searchPlaceholder}
              {...register("query")}
            />
          )}
          <button
            className={styles.mobileSearchButton}
            type="submit"
            aria-label={data.buttonSearchAriaLabel}
            onClick={handleSearchClick}
          >
            <ReactSVG src={lensIcon} />
          </button>
        </div>
      </form>
      {!isSearchExpanded && displayFilters && (
        <div className={styles.filtersContainer}>
          <Filter
            projectsNewsInsightsAllFilter={data.projectsNewsInsightsAllFilter}
            projectsNewsInsightsFilterLabel={data.projectsNewsInsightsFilterLabel}
            projectsNewsInsightsTwoFilters={data.projectsNewsInsightsTwoFilters}
            filterArticles={filterArticles}
            changeCurrentPage={changeCurrentPage}
            articles={articles}
            color="white"
          />
        </div>
      )}
    </section>
  ) : (
    <section className={styles.container}>
      <form ref={searchFormRef} method="get" className={styles.searchForm} action={`/${searchRequestSlug}`}>
        <div className={styles.formElements}>
          <input
            id="searchWord"
            type="text"
            name="query"
            className={styles.searchInput}
            defaultValue={searchTerms}
            placeholder={data.searchPlaceholder}
            {...register("query")}
          />
          <button className={styles.searchButton} type="submit" aria-label={data.buttonSearchAriaLabel}>
            <ReactSVG src={lensIcon} />
          </button>
        </div>
      </form>
      {displayFilters && (
        <div className={styles.filtersContainer}>
          <Filter
            projectsNewsInsightsAllFilter={data.projectsNewsInsightsAllFilter}
            projectsNewsInsightsFilterLabel={data.projectsNewsInsightsFilterLabel}
            projectsNewsInsightsTwoFilters={data.projectsNewsInsightsTwoFilters}
            filterArticles={filterArticles}
            changeCurrentPage={changeCurrentPage}
            articles={articles}
            color="white"
          />
        </div>
      )}
    </section>
  )
}

export const fragment = graphql`
  fragment PniCoverFields on DatoCmsPniPage {
    searchPlaceholder
    buttonSearchAriaLabel
    projectsNewsInsightsAllFilter
    projectsNewsInsightsFiltrers {
      label
    }
    projectsNewsInsightsFilterLabel
    projectsNewsInsightsTwoFilters {
      label
    }
  }
`

export default Cover
